import { Controller } from "@hotwired/stimulus"
import Dropzone from "dropzone"
import { Turbo } from "@hotwired/turbo-rails"

Dropzone.autoDiscover = false

// Meeting Reviews drop target (ISSUE-3853). Each file uploads in its own
// request; the server answers with a Turbo Stream that adds the staged call to
// the list, so a finished upload's card is removed. A rejected file's card turns
// red with the reason until dismissed. The zone watches the call list and shows
// the large "first call" prompt only while the list is empty; it toggles rather
// than re-renders, because replacing the element would cancel queued uploads.
export default class extends Controller {
  static targets = ["previews", "largePrompt", "compactPrompt"]
  static classes = ["large", "compact"]
  static values = {
    url: String,
    paramName: String,
    acceptedFiles: String,
    maxFilesize: Number,
    listId: String,
  }

  connect() {
    this.dropzone = new Dropzone(this.element, {
      url: this.urlValue,
      paramName: this.paramNameValue,
      acceptedFiles: this.acceptedFilesValue,
      maxFilesize: this.maxFilesizeValue,
      filesizeBase: 1024,
      parallelUploads: 3,
      autoProcessQueue: true,
      createImageThumbnails: false,
      clickable: this.element,
      previewsContainer: this.previewsTarget,
      previewTemplate: this.previewTemplate(),
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        Accept: "text/vnd.turbo-stream.html",
      },
      dictInvalidFileType: "Transcripts must be a .txt, .md, .vtt or .srt file.",
      dictFileTooBig: `Transcripts must be ${this.maxFilesizeValue} MB or smaller.`,
      dictResponseError: "Upload failed. Please try again.",
    })

    this.dropzone.on("success", (file, response) => {
      Turbo.renderStreamMessage(response)
      this.dropzone.removeFile(file)
    })
    this.dropzone.on("error", (file) => this.markFailed(file))

    this.watchList()
  }

  disconnect() {
    this.listObserver?.disconnect()
    this.dropzone?.destroy()
  }

  watchList() {
    const list = document.getElementById(this.listIdValue)
    if (!list) return

    this.listObserver = new MutationObserver(() => this.sizeFor(list))
    this.listObserver.observe(list, { childList: true })
    this.sizeFor(list)
  }

  sizeFor(list) {
    const empty = list.children.length === 0
    this.largePromptTarget.classList.toggle("d-none", !empty)
    this.compactPromptTarget.classList.toggle("d-none", empty)
    this.element.classList.remove(...(empty ? this.compactClasses : this.largeClasses))
    this.element.classList.add(...(empty ? this.largeClasses : this.compactClasses))
  }

  markFailed(file) {
    const card = file.previewElement
    if (!card) return

    card.classList.remove("bg-white")
    card.classList.add("bg-danger-subtle", "border-danger")
    card.querySelector("[data-progress]")?.classList.add("d-none")
  }

  previewTemplate() {
    return `
      <div class="d-flex align-items-center gap-3 small mt-2 py-2 px-3 rounded-3 border bg-white">
        <span class="fw-semibold text-truncate" data-dz-name></span>
        <div class="progress progress-sm flex-grow-1" data-progress>
          <div class="progress-bar" role="progressbar" data-dz-uploadprogress></div>
        </div>
        <span class="text-danger-emphasis ms-auto text-end" data-dz-errormessage></span>
        <button type="button" class="btn-close btn-sm flex-shrink-0" aria-label="Dismiss" data-dz-remove></button>
      </div>
    `
  }
}
